import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import { API_BASE_URL } from "../utils/urls.js";

const ServerTimeEntryForm = () => {
  const [formData, setFormData] = useState({
    case_id: "",
    entry_date: "",
    hours: "",
    description: "",
    expense_amount: "",
  });
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitStatus, setSubmitStatus] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAgentCases = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Authentication token not found.");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/server/my-cases`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // Map the API data to match form select fields
        const agentCases = response.data.data.map((c) => ({
          id: c.id,
          case_name: c.case_name,
          case_number: c.case_number || "N/A",
        }));

        setCases(agentCases);
      } catch (err) {
        console.error("Error fetching agent cases:", err);
        setError("Failed to load cases. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchAgentCases();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus("submitting");
    const token = localStorage.getItem("token");

    try {
      await axios.post(`${API_BASE_URL}/time-entries`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubmitStatus("success");
      setTimeout(() => navigate("/server/time-entries"), 1500);
    } catch (err) {
      setSubmitStatus("error");
      setError(err.response?.data?.message || "Submission failed. Please try again.");
    }
  };

  if (loading)
    return <div className="p-6 text-center">Loading form...</div>;
  if (error && submitStatus !== "submitting")
    return <div className="p-6 text-center text-red-500">{error}</div>;

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">
        Log Time & Expenses
      </h1>
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow-sm p-6 space-y-6"
      >
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Select Case
          </label>
          <select
            name="case_id"
            value={formData.case_id}
            onChange={handleChange}
            required
            className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-4 focus:ring-indigo-200 block w-full p-1.5 mt-2"
          >
            <option value="">Choose a case</option>
            {cases.map((caseItem) => (
              <option key={caseItem.id} value={caseItem.id}>
                {caseItem.case_name} ({caseItem.case_number})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Date
          </label>
          <input
            type="date"
            name="entry_date"
            value={formData.entry_date}
            onChange={handleChange}
            required
            className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-4 focus:ring-indigo-200 block w-full p-1.5 mt-2"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Hours Logged
          </label>
          <input
            type="number"
            name="hours"
            value={formData.hours}
            onChange={handleChange}
            required
            className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-4 focus:ring-indigo-200 block w-full p-1.5 mt-2"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Expense Amount
          </label>
          <input
            type="number"
            name="expense_amount"
            value={formData.expense_amount}
            onChange={handleChange}
            className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-4 focus:ring-indigo-200 block w-full p-1.5 mt-2"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Description
          </label>
          <textarea
            name="description"
            rows="3"
            value={formData.description}
            onChange={handleChange}
            className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-4 focus:ring-indigo-200 block w-full p-1.5 mt-2"
          ></textarea>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate("/cases")}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition duration-300"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitStatus === "submitting"}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition duration-300 disabled:opacity-50"
          >
            {submitStatus === "submitting" ? "Saving..." : "Save Entry"}
          </button>
        </div>

        {submitStatus === "success" && (
          <p className="mt-4 text-green-500 text-center">
            Time entry saved successfully!
          </p>
        )}
        {submitStatus === "error" && (
          <p className="mt-4 text-red-500 text-center">{error}</p>
        )}
      </form>
    </div>
  );
};

export default ServerTimeEntryForm;
